import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import { addDays, nextBusinessDay, startOfDay } from './dates';
import { downloadIcs } from './downloadIcs';
import { buildIcsCalendar, calendarEventFromStep } from './ics';
import { type CalendarStep } from './steps';
import styles from '../DevelopmentPlan.module.scss';

type AddAllStepsDialogProps = {
  open: boolean;
  steps: CalendarStep[];
  planUrl: string;
  onClose: () => void;
  onPlaced: (entries: Array<{ id: string; date: Date }>) => void;
  initialStart?: Date | null;
  today?: Date;
};

function dateFromPicker(value: Moment | null): Date | null {
  if (!value?.isValid()) {
    return null;
  }
  return value.clone().startOf('day').toDate();
}

export const AddAllStepsDialog: React.FC<AddAllStepsDialogProps> = ({
  open,
  steps,
  planUrl,
  onClose,
  onPlaced,
  initialStart,
  today,
}) => {
  const [start, setStart] = useState<Date>(() =>
    nextBusinessDay(today ?? new Date())
  );
  const [overrides, setOverrides] = useState<Record<string, Date>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setStart(
        initialStart
          ? startOfDay(initialStart)
          : nextBusinessDay(today ?? new Date())
      );
      setOverrides({});
      setNotice(null);
    }
    wasOpenRef.current = open;
  }, [open, initialStart, today]);

  const rows = steps.map((step, index) => ({
    step,
    date: overrides[step.id] ?? addDays(start, index * 7),
  }));

  const changeStart = (value: Moment | null) => {
    const next = dateFromPicker(value);
    if (!next) {
      return;
    }
    setStart(startOfDay(next));
    setOverrides({});
  };

  const changeRow = (stepId: string, value: Moment | null) => {
    const next = dateFromPicker(value);
    if (!next) {
      return;
    }
    setOverrides((current) => ({ ...current, [stepId]: next }));
  };

  const downloadAll = () => {
    if (rows.length === 0) {
      return;
    }
    const events = rows.map((row) =>
      calendarEventFromStep(row.step, row.date, planUrl)
    );
    downloadIcs('utviklingsplan.ics', buildIcsCalendar(events, new Date()));
    onPlaced(rows.map((row) => ({ id: row.step.id, date: row.date })));
    setNotice('Kalenderfilen er lastet ned.');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Legg alle steg i kalenderen</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            Hvert første steg og neste steg blir en heldagshendelse, med én uke
            mellom. Påminnelse kl. 09. Datoene under er det som legges inn.
            Kalenderen oppdateres ikke av seg selv hvis teksten endres.
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            Google Kalender kan bare ta ett steg om gangen fra lenken. Filen
            åpnes i Apple Kalender og Outlook. I Google Kalender importerer du
            filen, eller bruker ikonet på hvert steg.
          </Typography>
          <DatePicker
            label="Første dag"
            value={moment(start)}
            format="DD.MM.YYYY"
            onChange={changeStart}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                helperText:
                  'Endrer du første dag, settes alle datoene på nytt med én uke mellom.',
              },
            }}
          />
          <Stack spacing={1.5}>
            {rows.map((row) => (
              <Stack
                key={row.step.id}
                spacing={0.5}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {row.step.context}
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {row.step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {row.step.text}
                </Typography>
                <DatePicker
                  label="Dag"
                  value={moment(row.date)}
                  format="DD.MM.YYYY"
                  onChange={(value) => changeRow(row.step.id, value)}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Stack>
            ))}
          </Stack>
          {notice && <Typography variant="body2">{notice}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onClose}
        >
          Lukk
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={downloadAll}
          disabled={rows.length === 0}
        >
          {`Last ned ${rows.length} steg`}
        </button>
      </DialogActions>
    </Dialog>
  );
};
